import { FEATURE_KEYS, MODULE_KEYS } from '../routes/ids.js';
import { POSITION_TRANSFER_TABLE } from './table-registry.js';
const tk = (value) => value;
const fieldId = (value) => value;
function field(name, fieldType, required = false) {
    return {
        id: fieldId(`field.positionTransfer.${name}`),
        name,
        fieldType,
        labelKey: tk(`card.positionTransfers.fields.${name}`),
        ...(fieldType !== 'hidden' ? { helpTextKey: tk(`helpText.field.positionTransfer.${name}`) } : {}),
        value: { hiddenValuePolicy: 'unregister', submitBehavior: 'include' },
        ...(required
            ? {
                validation: {
                    rules: [{ ruleId: 'required', messageKey: tk('validation.required') }],
                    validateWhen: 'submit',
                },
            }
            : {}),
        layout: { md: fieldType === 'positionTransferLots' || fieldType === 'textarea' ? 12 : 6 },
    };
}
function action(workflow) {
    const id = `action.investment.positionTransfers.${workflow}`;
    const source = [
        ...(POSITION_TRANSFER_TABLE.pageActions ?? []),
        ...(POSITION_TRANSFER_TABLE.rowActions ?? []),
    ].find((candidate) => String(candidate.id) === id);
    if (!source?.commandId)
        throw new Error(`Missing canonical Position Transfer action: ${id}`);
    return {
        ...source,
        method: source.method ?? 'POST',
        invalidationTargets: source.invalidationTargets ?? ['resource.position-transfers'],
        submitBehavior: 'form_submit',
        submitConfig: {
            validatesForm: true,
            requiresDirty: workflow === 'request',
            requiresValid: true,
            resetBehavior: 'toServerValues',
            ...(workflow === 'request'
                ? { payloadBuilderId: 'builder.positionTransfer.request' }
                : {
                    includeFields: [
                        fieldId('field.positionTransfer.id'),
                        fieldId('field.positionTransfer.expectedVersion'),
                        fieldId('field.positionTransfer.reason'),
                    ],
                }),
        },
        payloadSchemaRef: (workflow === 'request'
            ? '#/schemas/command.positionTransfers.request'
            : '#/schemas/command.positionTransfers.decision'),
        visibilityCondition: {
            operator: 'equals',
            field: 'field.positionTransfer.workflowKind',
            value: workflow,
        },
    };
}
/**
 * One resolved form binds all position-transfer lifecycle commands. The lot
 * allocation field is a registered specialist adapter; its query and row
 * semantics are declared here rather than reconstructed by the route.
 */
export const POSITION_TRANSFER_WORKFLOW_FORM = {
    id: 'form.investment.positionTransfer.workflow',
    schemaVersion: 1,
    module: MODULE_KEYS.PORTFOLIO_MGMT,
    feature: FEATURE_KEYS.DATA_POSITION_TRANSFERS,
    privacyClassification: 'FINANCIAL',
    telemetryId: 'experience.form.positionTransfer.workflow',
    accessibilityLabelKey: tk('card.positionTransfers.title'),
    availability: 'AVAILABLE',
    owner: 'investment',
    titleKey: tk('card.positionTransfers.title'),
    descriptionKey: tk('card.positionTransfers.description'),
    mode: 'create',
    fields: [
        {
            ...field('workflowKind', 'hidden'),
            value: {
                hiddenValuePolicy: 'preserve',
                submitBehavior: 'include',
                retainWhenHidden: true,
            },
        },
        {
            ...field('id', 'hidden'),
            value: {
                hiddenValuePolicy: 'preserve',
                submitBehavior: 'include',
                retainWhenHidden: true,
            },
        },
        {
            ...field('expectedVersion', 'hidden'),
            value: {
                hiddenValuePolicy: 'preserve',
                submitBehavior: 'exclude',
                outputOnly: true,
                retainWhenHidden: true,
            },
        },
        field('sourcePortfolioAccountId', 'text', true),
        field('instrumentSymbol', 'text', true),
        field('businessReference', 'text', true),
        {
            ...field('reason', 'textarea', true),
            state: {
                visibilityCondition: {
                    operator: 'in',
                    field: 'field.positionTransfer.workflowKind',
                    values: ['request', 'cancel', 'reverse'],
                },
            },
        },
        {
            ...field('allocationPlan', 'positionTransferLots', true),
            state: {
                visibilityCondition: {
                    operator: 'equals',
                    field: 'field.positionTransfer.workflowKind',
                    value: 'request',
                },
            },
        },
    ],
    actions: ['request', 'approve', 'execute', 'cancel', 'reverse'].map(action),
    submission: { defaultCommandId: 'cmd.investment.positionTransfers.request' },
};
